import { useRef, useState, useEffect } from "react";
import { atom, useSetRecoilState } from "recoil";
import firebase from "firebase/compat/app";
import { consola } from "consola";
import moment from "moment";
import { getkCal } from "../components/LibraryFunctions";

export const ClientNutritionChartDataState = atom({
  key: "ClientNutritionChartDataState",
  default: null,
});

function useClientNutritionChartData({
  uid,
  organizationId,
  duration,
  count,
  start,
  end,
  enabled = true,
}) {
  const subscription = useRef(null);

  const [data, setData] = useState(null);
  const setClientNutritionChartData = useSetRecoilState(
    ClientNutritionChartDataState,
  );

  // Check if inputs are valid
  const isValid = Boolean(uid && organizationId) && enabled;
  const startMs = start instanceof Date ? start.getTime() : start;
  const endMs = end instanceof Date ? end.getTime() : end;

  useEffect(() => {
    // Clean up previous subscription if it exists
    if (subscription.current) {
      subscription.current();
      subscription.current = null;
    }

    if (!enabled) {
      setData(null);
      setClientNutritionChartData(null);
      return undefined;
    }

    // Only proceed with Firebase calls if both uid and organizationId are valid
    if (!isValid) {
      consola.debug(
        "useClientNutritionChartData: uid and organizationId are required",
      );
      setData(null);
      setClientNutritionChartData(null);
      return undefined;
    }

    let ref = null;
    if (start && end) {
      ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(uid)
        .collection("reportHealth")
        .where("createdOn", ">=", start)
        .where("createdOn", "<=", end)
        .orderBy("createdOn");
    } else if (duration) {
      const rangeStart = moment().subtract(duration, "days").startOf("day").toDate();
      const rangeEnd = moment().endOf("day").toDate();

      ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(uid)
        .collection("reportHealth")
        .where("createdOn", ">=", rangeStart)
        .where("createdOn", "<=", rangeEnd)
        .orderBy("createdOn");
    } else {
      ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(uid)
        .collection("reportHealth")
        .limit(count)
        .orderBy("createdOn", "desc");
    }

    let mounted = true;
    subscription.current = ref.onSnapshot((snapshot) => {
      if (!mounted) return;
      consola.debug("++++++ SNAPSHOT -> useClientNutritionChartData");

      const finalData = snapshot.docs.map((d) => {
        const doc = d.data();
        const rd = doc.reportData || {};
        const micro =
          rd.micronutrients && typeof rd.micronutrients === "object"
            ? rd.micronutrients
            : {};
        const fiberVal = typeof micro.fiber === "number" ? micro.fiber : 0;

        let kCal = 0;
        if (typeof rd.kCal === "number" && rd.kCal > 0) {
          kCal = rd.kCal;
        } else if (rd.carbs > 0 || rd.fats > 0 || rd.proteins > 0) {
          kCal = getkCal({
            carbs: rd.carbs,
            fats: rd.fats,
            proteins: rd.proteins,
          });
        }

        return {
          ...rd,
          kCal: parseFloat(Number(kCal).toFixed(2)),
          carbs: parseFloat(Number(rd.carbs || 0).toFixed(2)),
          fats: parseFloat(Number(rd.fats || 0).toFixed(2)),
          proteins: parseFloat(Number(rd.proteins || 0).toFixed(2)),
          weight: parseFloat(Number(rd.weight || 0).toFixed(2)),
          water: parseFloat(Number(rd.water || 0).toFixed(2)),
          sleepHours: parseFloat(Number(rd.sleepHours || 0).toFixed(1)),
          fiber: parseFloat(Number(fiberVal).toFixed(2)),
          steps: parseFloat(Number(rd.steps || 0).toFixed(0)),
          averageHeartRate: parseFloat(Number(rd.averageHeartRate || 0).toFixed(0)),
          createdOn: doc.createdOn.toDate(),
          createdOnString: moment(doc.createdOn.toDate()).format("D MMM"),
        };
      });

      const sorted = finalData.sort((a, b) => {
        return a.createdOn - b.createdOn;
      });
      setData(sorted);
      setClientNutritionChartData(sorted);
    });

    return () => {
      mounted = false;
      if (subscription.current) {
        subscription.current();
        subscription.current = null;
      }
    };
  }, [
    uid,
    organizationId,
    isValid,
    duration,
    count,
    enabled,
    start,
    end,
    startMs,
    endMs,
    setClientNutritionChartData,
  ]);

  return { nutritionChartData: data };
}

export default useClientNutritionChartData;
